import * as React from 'react';
import { FormControl, MenuItem, Select, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CustomSetting, Setting } from 'types/types';

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1
  },
  rangeContainer: {
    display: 'flex',
    flex: 0.3
  },
  inputText: {
    fontSize: 13,
    textAlign: 'left',
    paddingTop: theme.spacing(1.9),
    paddingBottom: theme.spacing(1.5)
  },
  divider: {
    margin: '15px 0',
    paddingTop: theme.spacing(1)
  },
  input: {
    width: '65%',
    display: 'flex',
    paddingTop: theme.spacing(1.4),
    marginLeft: theme.spacing(1.25),
    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      display: 'none'
    },
    '& input[type=number]': {
      MozAppearance: 'textfield'
    }
  },
  dropdown: {
    display: 'flex',
    flex: 0.7,
    fontSize: 13,
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1),
    marginLeft: theme.spacing(2)
  },
  startRange: {
    width: '20%',
    fontSize: 13,
    textAlign: 'right',
    paddingTop: theme.spacing(3.5),
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginRight: theme.spacing(1.25)
  },
  endRange: {
    width: '65%',
    fontSize: 13,
    textAlign: 'left',
    paddingTop: theme.spacing(3.5),
    paddingLeft: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginLeft: theme.spacing(1.25)
  }
}));

type ProjectStatusSectionProps = {
  index: number;
  settings: Setting[];
  setSettings: React.Dispatch<React.SetStateAction<Setting[]>>;
  inputSetting: CustomSetting;
};

export const ProjectStatusSection: React.FC<ProjectStatusSectionProps> = ({
  index,
  settings,
  setSettings,
  inputSetting
}: ProjectStatusSectionProps) => {
  const classes = useStyles();

  const startValue = settings.length ? +settings[index].key.split(':')[0] : 0;
  const initialEndValue = settings.length
    ? settings[index].key.split(':')[1]
    : '100';

  const matchObject = inputSetting.dropdownOptions?.find(
    (option) => Object.values(option)[0] === settings[index].value
  );
  const initialStatus = matchObject ? JSON.stringify(matchObject) : '';

  const [endValue, setEndValue] = React.useState<string>(initialEndValue);
  const [status, setStatus] = React.useState<string>(initialStatus);

  React.useEffect(() => {
    setEndValue(initialEndValue);
    setStatus(initialStatus);
  }, [initialEndValue, initialStatus]);

  const handleDropdownChange = React.useCallback(
    (event) => {
      const newStatus = event.target.value as string;
      setStatus(newStatus);
      const newValue = Object.values(JSON.parse(newStatus))[0];
      setSettings((prev) => {
        const updated = [...prev];
        updated[index]['value'] = newValue as string;
        return updated;
      });
    },
    [index, setSettings]
  );

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newEndValue = e.target.value ? +e.target.value : -1;

      const isInvalidEndRange = () => {
        if (startValue > newEndValue) return true;

        if (+initialEndValue === Math.round(newEndValue)) return true;

        const nextEndRange = +settings[index + 1].key.split(':')[1];
        if (newEndValue >= nextEndRange) return true;

        return false;
      };

      if (isInvalidEndRange()) {
        setEndValue(initialEndValue);
        return;
      }

      setSettings((prev: Setting[]) => {
        const updated = [...prev];
        const currRange = {
          key: `${Math.round(startValue)}:${Math.round(newEndValue)}`,
          value: Object.values(JSON.parse(status))[0] as string
        };

        const nextRange = updated[index + 1];
        const nextRangeKey = `${Math.round(newEndValue) + 1}:${
          nextRange.key.split(':')[1]
        }`;

        updated[index] = currRange;
        updated[index + 1] = { ...nextRange, key: nextRangeKey };
        return updated;
      });
    },
    [startValue, status, setSettings, index, settings, initialEndValue]
  );

  const renderInputComponent = () =>
    index === settings.length - 1 ? (
      <span className={classes.endRange}>{endValue}</span>
    ) : (
      <TextField
        size="small"
        type="number"
        error={startValue > +endValue}
        value={endValue}
        onChange={(e) => setEndValue(e.target.value)}
        onBlur={handleBlur}
        className={classes.input}
        InputProps={{
          classes: { input: classes.inputText }
        }}
      />
    );

  const renderDropdownComponent = () => (
    <FormControl className={classes.dropdown}>
      <Select
        size="small"
        value={status}
        onChange={handleDropdownChange}
        role="combobox"
      >
        {inputSetting.dropdownOptions?.map((option, index) => (
          <MenuItem
            key={index}
            value={JSON.stringify(option)}
            disabled={Boolean(
              settings.find(
                ({ value }: Setting) => value === Object.values(option)[0]
              )
            )}
          >
            <span style={{ fontSize: 13 }}>{Object.keys(option)[0]}</span>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <div className={classes.rootContainer} data-testid="root-element">
      <div className={classes.rangeContainer} data-testid="range-element">
        <span className={classes.startRange}>{startValue}</span>
        <span className={classes.divider}>-</span>
        {renderInputComponent()}
      </div>
      {renderDropdownComponent()}
    </div>
  );
};
